import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, FormLabel, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Pagination, Radio, RadioGroup, Select, TextField, Typography } from "@mui/material";
import {
  DataGrid,
  GridFooterContainer,
  useGridApiRef,
} from "@mui/x-data-grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import DownloadIcon from "@mui/icons-material/Download";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ClearIcon from "@mui/icons-material/Clear";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RemoveIcon from "@mui/icons-material/Remove";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoNotDisturbOffOutlinedIcon from "@mui/icons-material/DoNotDisturbOffOutlined";
import AlertManager from "../../shared/Alert/Alert";
import { getDCCostItemsRows } from "../../API/dcCostItemsApi";
import { approveDCCostBatch } from "../../API/dcCostBatchApproveApi";
import { draftDCCostBatch } from "../../API/dcCostBatchDraftApi";
import { rejectDCCostBatch } from "../../API/dcCostBatchRejectApi";
import { buildThresholdUpdatePayload, modifyDCCostThreshold } from "../../API/dcCostModifyThresholdApi";
import { restoreSkuStatus, updateSkuStatus } from "../../API/dcSkuStatusApi";
import { exportRowsToXlsx } from "../../shared/utils/exportUtils";
import resultsClasses from "../LandedCostResults/LandedCostResults.module.css";
import tableClasses from "../LandedCostUpload/LandedCostUpload.module.css";
import pageClasses from "./DCCostFlow.module.css";
import { useNavigate, useSearchParams } from "react-router-dom";

export { buildThresholdUpdatePayload } from "../../API/dcCostModifyThresholdApi";

const currencyFormatter = (value) =>
  value === null || value === undefined
    ? "-"
    : `$${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

const formatFobUnit = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  return typeof value === "string" ? value : currencyFormatter(value);
};

const percentFormatter = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "number") {
    return `${value}%`;
  }

  return `${value}%`;
};

const normalizeFilterText = (value) => `${value ?? ""}`.trim().toLowerCase();

const normalizeSortValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number") {
    return value;
  }

  if (typeof value === "boolean") {
    return value ? 1 : 0;
  }

  const numericValue = Number(value);
  if (!Number.isNaN(numericValue) && `${value}`.trim() !== "") {
    return numericValue;
  }

  return `${value}`.toLowerCase();
};

export const sortDCCostRowsByModel = (rows = [], sortModel = []) => {
  if (!Array.isArray(rows) || !rows.length || !Array.isArray(sortModel) || !sortModel.length) {
    return Array.isArray(rows) ? rows : [];
  }

  const sortSpec = sortModel[0];
  if (!sortSpec?.field || !sortSpec?.sort) {
    return rows;
  }

  const direction = sortSpec.sort === "desc" ? -1 : 1;

  return [...rows].sort((leftRow, rightRow) => {
    const leftValue = normalizeSortValue(leftRow?.[sortSpec.field]);
    const rightValue = normalizeSortValue(rightRow?.[sortSpec.field]);

    if (leftValue === rightValue) {
      return 0;
    }

    if (leftValue === null) {
      return 1 * direction;
    }

    if (rightValue === null) {
      return -1 * direction;
    }

    if (typeof leftValue === "number" && typeof rightValue === "number") {
      return (leftValue - rightValue) * direction;
    }

    return `${leftValue}`.localeCompare(`${rightValue}`, undefined, { numeric: true, sensitivity: "base" }) * direction;
  });
};

export const filterDCCostRowsByFrozenColumns = (rows = [], filters = {}) => {
  if (!Array.isArray(rows) || !rows.length) {
    return Array.isArray(rows) ? rows : [];
  }

  const parsedSkuFilter = normalizeFilterText(filters.parsedSku);
  const batchIdFilter = normalizeFilterText(filters.batchId);
  const hqLineFilter = normalizeFilterText(filters.hqLine);

  if (!parsedSkuFilter && !batchIdFilter && !hqLineFilter) {
    return rows;
  }

  return rows.filter((row) => {
    const parsedSku = normalizeFilterText(row?.parsedSku);
    const batchId = normalizeFilterText(row?.batchId);
    const hqLine = normalizeFilterText(row?.hqLine);

    const parsedSkuMatches = !parsedSkuFilter || parsedSku.includes(parsedSkuFilter);
    const batchIdMatches = !batchIdFilter || batchId.includes(batchIdFilter);
    const hqLineMatches = !hqLineFilter || hqLine.includes(hqLineFilter);

    return parsedSkuMatches && batchIdMatches && hqLineMatches;
  });
};

const MARGIN_STRATEGY_LABELS = {
  GP_NEUTRAL: "GP Neutral",
  MAINTAIN_PRICE: "Maintain Price",
  MAINTAIN_MARGIN: "Maintain Margin",
};

const MARGIN_STRATEGY_OPTIONS = Object.keys(MARGIN_STRATEGY_LABELS).map((value) => ({
  value,
  label: MARGIN_STRATEGY_LABELS[value],
}));

const formatMarginStrategyType = (value) => MARGIN_STRATEGY_LABELS[value] || value || "-";

const APPROVAL_VALUE_SOURCES = {
  PROPOSED: "proposed",
  MODIFY: "modify",
};

const getTierPriceValue = (row, tier) => {
  const fieldCandidates = {
    red: ["currentDcRed", "redPrice", "currentRedTierPrice", "redTierPrice", "currentRedPrice"],
    blue: ["currentDcBlue", "bluePrice", "currentBlueTierPrice", "blueTierPrice", "currentBluePrice"],
    gold: ["currentDcGold", "goldPrice", "currentGoldTierPrice", "goldTierPrice", "currentGoldPrice"],
  }[tier] || [];

  for (const fieldName of fieldCandidates) {
    const value = row?.[fieldName];
    if (value !== null && value !== undefined && value !== "") {
      return value;
    }
  }

  return row?.currentDc ?? row?.proposedDc ?? null;
};

const TIER_METRIC_CANDIDATES = {
  gold: {
    price: ["newGoldPrice", "goldPrice", "newGoldTierPrice", "currentGoldTierPrice", "currentDcGold"],
    dc: ["goldDc", "goldDC", "newGoldPrice", "proposedDcGold"],
    oldMargin: ["oldGoldMarginPercentage", "oldGoldMarginPercent", "oldMarginPercent"],
    newMargin: ["newGoldMarginPercentage", "newGoldMarginPercent", "goldMarginPercent", "goldMarginPct", "newMarginPercent"],
    marginChange: ["goldMarginPercentageChange", "goldMarginPercentChange"],
  },
  blue: {
    price: ["newBluePrice", "bluePrice", "newBlueTierPrice", "currentBlueTierPrice", "currentDcBlue"],
    dc: ["blueDc", "blueDC", "newBluePrice", "proposedDcBlue"],
    oldMargin: ["oldBlueMarginPercentage", "oldBlueMarginPercent", "oldMarginPercent"],
    newMargin: ["newBlueMarginPercentage", "newBlueMarginPercent", "blueMarginPercent", "blueMarginPct", "newMarginPercent"],
    marginChange: ["blueMarginPercentageChange", "blueMarginPercentChange"],
  },
  red: {
    price: ["newRedPrice", "redPrice", "newRedTierPrice", "currentRedTierPrice", "currentDcRed"],
    dc: ["redDc", "redDC", "newRedPrice", "proposedDcRed"],
    oldMargin: ["oldRedMarginPercentage", "oldRedMarginPercent", "oldMarginPercent"],
    newMargin: ["newRedMarginPercentage", "newRedMarginPercent", "redMarginPercent", "redMarginPct", "newMarginPercent"],
    marginChange: ["redMarginPercentageChange", "redMarginPercentChange"],
  },
};

const getTierMetricValue = (row, tier, metric) => {
  const candidateFields = TIER_METRIC_CANDIDATES[tier]?.[metric] || [];

  for (const fieldName of candidateFields) {
    const value = row?.[fieldName];
    if (value !== null && value !== undefined && value !== "") {
      return value;
    }
  }

  return null;
};

const formatTierSummary = (row, tier, { includeCurrentPrice = false } = {}) => {
  const currentPrice = includeCurrentPrice ? currencyFormatter(getTierPriceValue(row, tier)) : null;
  const newPrice = currencyFormatter(getTierMetricValue(row, tier, "price"));
  const dcValue = currencyFormatter(getTierMetricValue(row, tier, "dc"));
  const oldMarginValue = percentFormatter(getTierMetricValue(row, tier, "oldMargin"));
  const newMarginValue = percentFormatter(getTierMetricValue(row, tier, "newMargin"));
  const changeMarginValue = percentFormatter(getTierMetricValue(row, tier, "marginChange"));

  return includeCurrentPrice
    ? `${currentPrice} / ${newPrice} / ${dcValue} / ${oldMarginValue} / ${newMarginValue} / ${changeMarginValue}`
    : `${newPrice} / ${dcValue} / ${oldMarginValue} / ${newMarginValue} / ${changeMarginValue}`;
};

const THRESHOLD_EDITABLE_FIELDS = new Set([
  "modifyMarginStrategyType",
  "modifyNewMarginPercent",
  "modifyNewMargin",
  "modifyProposedDc",
  "modifyNewDc",
]);

const getThresholdEditableColumnClasses = (field, isThresholdEditing) => {
  if (!isThresholdEditing || !THRESHOLD_EDITABLE_FIELDS.has(field)) {
    return {};
  }

  return {
    headerClassName: "threshold-editable-column-header",
    cellClassName: "threshold-editable-column-cell",
  };
};

const normalizeThresholdValue = (value) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const normalizedValue = Number(value);
  return Number.isNaN(normalizedValue) ? value : normalizedValue;
};

const DETAIL_ROWS_PER_PAGE_OPTIONS = [100, 75, 50, 25];

const resolveResponseField = (responseRow, fieldName, fallbackValue) =>
  Object.prototype.hasOwnProperty.call(responseRow || {}, fieldName) ? responseRow[fieldName] : fallbackValue;

const normalizeRowStatus = (row = {}) => `${row.status ?? row.repricingStatus ?? ""}`.trim().toUpperCase();

export const canModifyThresholdRow = (row = {}) => {
  const rowStatus = normalizeRowStatus(row);
  return rowStatus === "ESTIMATED" || rowStatus === "ESCALATED";
};

export const canApproveRow = (row = {}) => {
  const rowStatus = normalizeRowStatus(row);
  return rowStatus === "ESTIMATED" || rowStatus === "ESCALATED";
};

const resolveProposedDc = (row = {}) => {
  if (row.proposedDc !== null && row.proposedDc !== undefined && row.proposedDc !== "") {
    return row.proposedDc;
  }

  if (row.marginStrategyType === "Gold") {
    return row.proposedDcGold ?? row.proposedDcBlue ?? row.proposedDcRed ?? null;
  }

  if (row.marginStrategyType === "Blue") {
    return row.proposedDcBlue ?? row.proposedDcGold ?? row.proposedDcRed ?? null;
  }

  if (row.marginStrategyType === "Red") {
    return row.proposedDcRed ?? row.proposedDcBlue ?? row.proposedDcGold ?? null;
  }

  return row.proposedDcGold ?? row.proposedDcBlue ?? row.proposedDcRed ?? null;
};

export const normalizeDetailRow = (row, index = 0) => ({
  ...row,
  id: row.id || row.detailId || row.parsedSku || row.batchId || row.batchID || row.batch_id || row.batchid || `dc-cost-detail-${index}`,
  status: row.status || row.repricingStatus || "",
  parsedSku: row.parsedSku || row.napaSku || row.sku || row.skuId || "",
  batchId: row.batchId || row.batchID || row.batch_id || row.batchid || "",
  fieldLine: row.fieldLine || row.field_line || "",
  napaSkuNumber: row.napaSkuNumber || row.napaSku || row.parsedSku || row.sku || row.skuId || "",
  apgVendorNumber: row.apgVendorNumber || row.asgVendorAccountNumber || "",
  vendorDescription: row.vendorDescription || row.vendorName || "",
  mfgPartNumber: row.mfgPartNumber || row.mfgPart || "",
  percentDemand: row.percentDemand ?? row.percentageOfDemand ?? null,
  oldFobUnit: row.oldFobUnit ?? null,
  oldLandedCost: row.oldLandedCost ?? row.old_landed_cost ?? row.oldLandingCost ?? row.old_landing_cost ?? null,
  newLandingCost: row.newLandingCost ?? row.new_landing_cost ?? row.newLandedCost ?? row.new_landed_cost ?? null,
  percentChangeLanded: row.percentChangeLanded ?? row.percentChangeInLanded ?? null,
  currentAsgFob: row.currentAsgFob ?? row.newAsgFob ?? null,
  newAsgFob: row.newAsgFob ?? row.currentAsgFob ?? null,
  marginStrategyType: row.marginStrategyType || row.marginStrategy || row.strategy || "",
  modifyMarginStrategyType: row.modifyMarginStrategyType || row.modifyMarginStrategy || row.modifyStrategy || "",
  proposedDc: row.proposedDc ?? resolveProposedDc(row),
  modifyProposedDc:
    row.modifyProposedDc ??
    row.modifiedProposedDc ??
    row.modifyNewDcCost ??
    row.newDcCost ??
    row.dcDollar ??
    row.dcCostDollar ??
    null,
  oldMarginPercent: row.oldMarginPercent ?? row.oldMarginPercentage ?? null,
  modifyNewMarginPercent:
    row.modifyNewMarginPercent ??
    row.modifyNewMarginPercentage ??
    row.modifiedNewMarginPercent ??
    row.marginPercent ??
    null,
  newMarginPercent: row.newMarginPercent ?? row.newMarginPercentage ?? null,
  modifyNewMargin: row.modifyNewMargin ?? row.modifyNewMarginDollar ?? row.modifiedNewMargin ?? row.marginDollar ?? row.newMarginDollar ?? null,
  percentChangeMargin: row.percentChangeMargin ?? row.percentageChangeInMargin ?? null,
  modifyNewDc: row.modifyNewDc ?? row.modifyMarginThreshold ?? row.dcPercent ?? row.dcCostPercentage ?? null,
  currentDcRed: row.currentDcRed ?? row.redPrice ?? null,
  currentDcBlue: row.currentDcBlue ?? row.bluePrice ?? null,
  currentDcGold: row.currentDcGold ?? row.goldPrice ?? null,
  newGoldPrice: row.newGoldPrice ?? row.goldPrice ?? row.currentDcGold ?? null,
  goldDc: row.goldDc ?? row.goldDC ?? row.newGoldPrice ?? row.proposedDcGold ?? null,
  oldGoldMarginPercentage: row.oldGoldMarginPercentage ?? row.oldGoldMarginPercent ?? row.oldMarginPercent ?? null,
  newGoldMarginPercentage: row.newGoldMarginPercentage ?? row.newGoldMarginPercent ?? row.goldMarginPercent ?? row.goldMarginPct ?? row.newMarginPercent ?? null,
  goldMarginPercentageChange: row.goldMarginPercentageChange ?? row.goldMarginPercentChange ?? null,
  newBluePrice: row.newBluePrice ?? row.bluePrice ?? row.currentDcBlue ?? null,
  blueDc: row.blueDc ?? row.blueDC ?? row.newBluePrice ?? row.proposedDcBlue ?? null,
  oldBlueMarginPercentage: row.oldBlueMarginPercentage ?? row.oldBlueMarginPercent ?? row.oldMarginPercent ?? null,
  newBlueMarginPercentage: row.newBlueMarginPercentage ?? row.newBlueMarginPercent ?? row.blueMarginPercent ?? row.blueMarginPct ?? row.newMarginPercent ?? null,
  blueMarginPercentageChange: row.blueMarginPercentageChange ?? row.blueMarginPercentChange ?? null,
  newRedPrice: row.newRedPrice ?? row.redPrice ?? row.currentDcRed ?? null,
  redDc: row.redDc ?? row.redDC ?? row.newRedPrice ?? row.proposedDcRed ?? null,
  oldRedMarginPercentage: row.oldRedMarginPercentage ?? row.oldRedMarginPercent ?? row.oldMarginPercent ?? null,
  newRedMarginPercentage: row.newRedMarginPercentage ?? row.newRedMarginPercent ?? row.redMarginPercent ?? row.redMarginPct ?? row.newMarginPercent ?? null,
  redMarginPercentageChange: row.redMarginPercentageChange ?? row.redMarginPercentChange ?? null,
});

const extractDetailRows = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  return response?.content ?? response?.rows ?? response?.detailRows ?? response?.data ?? [];
};

export const applyModifyThresholdResponseToRow = (row, responseRow = {}) => {
  const responseDetailId = responseRow?.detailId ?? responseRow?.id ?? null;
  const rowDetailId = row?.detailId ?? row?.id ?? null;
  const matchesById = responseDetailId !== null && rowDetailId !== null && `${responseDetailId}` === `${rowDetailId}`;
  const matchesBySku =
    responseRow?.sku &&
    row?.parsedSku &&
    `${responseRow.sku}` === `${row.parsedSku}` &&
    `${responseRow.hqLine || ""}` === `${row.hqLine || ""}`;

  if (!matchesById && !matchesBySku) {
    return row;
  }

  return {
    ...row,
    modifyMarginStrategyType: resolveResponseField(responseRow, "modifiedMarginStrategy", row.modifyMarginStrategyType),
    modifyNewMarginPercent: resolveResponseField(responseRow, "modifiedMarginPercentage", row.modifyNewMarginPercent),
    modifyNewMargin: resolveResponseField(responseRow, "modifiedMarginDollar", row.modifyNewMargin),
    modifyProposedDc: resolveResponseField(responseRow, "modifiedDcCost", row.modifyProposedDc),
    modifyNewDc: resolveResponseField(responseRow, "modifiedDcPercentage", row.modifyNewDc),
  };
};

export const applyModifyThresholdResponses = (rows = [], responseRows = []) => {
  if (!Array.isArray(rows) || !rows.length || !Array.isArray(responseRows) || !responseRows.length) {
    return Array.isArray(rows) ? rows : [];
  }

  return rows.map((row) =>
    responseRows.reduce((currentRow, responseRow) => applyModifyThresholdResponseToRow(currentRow, responseRow), row)
  );
};

const PRIORITY_DETAIL_ROWS = [
  {
    id: "SKU-1001",
    parsedSku: "SKU-1001",
    hqLine: "H1",
    apgVendorNumber: "APG-8891",
    napaSkuNumber: "NS-55312",
    vendorDescription: "Priority autoparts",
    itemDescription: "Brake pad kit",
    mfgPartNumber: "MP-1001",
    status: "Pending",
    category: "Brakes",
    demand: 1500,
    percentDemand: 32,
    oldFobUnit: 22.5,
    oldFob: 33750,
    newLandingCost: 24.1,
    percentChangeLanded: 7.1,
    currentAsgFob: 23,
    newAsgFob: 24,
    currentDc: 28.5,
    marginStrategyType: "Gold",
    proposedDcGold: 32.5,
    proposedDcBlue: 30.5,
    proposedDcRed: 29.5,
    oldMargin: 45000,
    newMargin: 46300,
    oldMarginPercent: 18.4,
    newMarginPercent: 18.9,
    percentChangeMargin: 2.9,
    modifyMarginThreshold: 1.5,
    modifiedProposedDc: 31.8,
    modifiedNewMargin: 46050,
    modifiedNewMarginPercent: 18.7,
    linkToCostFile: "SKU-1001_item_cost_file.xlsx",
    modifyStrategy: "Gold safe",
    flag: false,
    flaggedReason: "",
    onHold: false,
    onHoldReason: "",
  },
  {
    id: "SKU-1002",
    parsedSku: "SKU-1002",
    hqLine: "H2",
    apgVendorNumber: "APG-8891",
    napaSkuNumber: "NS-55313",
    vendorDescription: "Priority autoparts",
    itemDescription: "Rotor front",
    mfgPartNumber: "MP-1002",
    status: "Flagged",
    category: "Brakes",
    demand: 950,
    percentDemand: 21,
    oldFobUnit: 18.2,
    oldFob: 17290,
    newLandingCost: 19.6,
    percentChangeLanded: 7.7,
    currentAsgFob: 18.5,
    newAsgFob: 19.4,
    currentDc: 24.1,
    marginStrategyType: "Blue",
    proposedDcGold: 27.5,
    proposedDcBlue: 25.5,
    proposedDcRed: 24.5,
    oldMargin: 38000,
    newMargin: 39050,
    oldMarginPercent: 17.8,
    newMarginPercent: 18.3,
    percentChangeMargin: 2.8,
    modifyMarginThreshold: 2.0,
    modifiedProposedDc: 25.0,
    modifiedNewMargin: 38880,
    modifiedNewMarginPercent: 18.1,
    linkToCostFile: "SKU-1002_item_cost_file.xlsx",
    modifyStrategy: "Blue adjust",
    flag: true,
    flaggedReason: "Cost spike over tolerance",
    onHold: false,
    onHoldReason: "",
  },
];

const REGULAR_DETAIL_ROWS = [
  {
    id: "SKU-2001",
    parsedSku: "SKU-2001",
    hqLine: "H1",
    apgVendorNumber: "APG-7721",
    napaSkuNumber: "NS-66300",
    vendorDescription: "Standard autoparts",
    itemDescription: "Oil filter",
    mfgPartNumber: "MP-2001",
    status: "Pending",
    category: "Filters",
    demand: 2100,
    percentDemand: 18,
    oldFobUnit: 4.2,
    oldFob: 8820,
    newLandingCost: 4.5,
    percentChangeLanded: 7.1,
    currentAsgFob: 4.3,
    newAsgFob: 4.6,
    currentDc: 6.1,
    marginStrategyType: "Red",
    proposedDcGold: 6.9,
    proposedDcBlue: 6.5,
    proposedDcRed: 6.2,
    modifyMarginThreshold: 1.0,
    modifiedProposedDc: 6.4,
    modifyStrategy: "Red cautious",
    flag: false,
    flaggedReason: "",
    onHold: false,
    onHoldReason: "",
  },
  {
    id: "SKU-2002",
    parsedSku: "SKU-2002",
    hqLine: "H2",
    apgVendorNumber: "APG-7722",
    napaSkuNumber: "NS-66301",
    vendorDescription: "Standard autoparts",
    itemDescription: "Cabin filter",
    mfgPartNumber: "MP-2002",
    status: "Partially approved",
    category: "Filters",
    demand: 1800,
    percentDemand: 16,
    oldFobUnit: 5.1,
    oldFob: 9180,
    newLandingCost: 5.4,
    percentChangeLanded: 5.9,
    currentAsgFob: 5.2,
    newAsgFob: 5.6,
    currentDc: 6.8,
    marginStrategyType: "Red",
    proposedDcGold: 7.6,
    proposedDcBlue: 7.2,
    proposedDcRed: 6.9,
    modifyMarginThreshold: 1.1,
    modifiedProposedDc: 7.0,
    modifyStrategy: "Red steady",
    flag: false,
    flaggedReason: "",
    onHold: true,
    onHoldReason: "Awaiting vendor confirmation",
  },
  {
    id: "SKU-2003",
    parsedSku: "SKU-2003",
    hqLine: "H3",
    apgVendorNumber: "APG-7723",
    napaSkuNumber: "NS-66302",
    vendorDescription: "Standard autoparts",
    itemDescription: "Air filter",
    mfgPartNumber: "MP-2003",
    status: "Escalated",
    category: "Filters",
    demand: 1600,
    percentDemand: 14,
    oldFobUnit: 4.9,
    oldFob: 7840,
    newLandingCost: 5.0,
    percentChangeLanded: 2.0,
    currentAsgFob: 5.0,
    newAsgFob: 5.2,
    currentDc: 6.5,
    marginStrategyType: "Red",
    proposedDcGold: 7.0,
    proposedDcBlue: 6.7,
    proposedDcRed: 6.6,
    modifyMarginThreshold: 0.8,
    modifiedProposedDc: 6.8,
    modifyStrategy: "Red review",
    flag: true,
    flaggedReason: "Margin needs review",
    onHold: false,
    onHoldReason: "",
  },
];

const NEW_DETAIL_ROWS = [
  {
    id: "SKU-3001",
    parsedSku: "SKU-3001",
    hqLine: "H1",
    apgVendorNumber: "APG-9901",
    napaSkuNumber: "NS-77312",
    vendorDescription: "New items vendor",
    itemDescription: "Starter kit",
    mfgPartNumber: "MP-3001",
    status: "Pending",
    category: "New items",
    demand: 900,
    percentDemand: 28,
    oldFobUnit: 12.5,
    oldFob: 11250,
    newLandingCost: 13.1,
    percentChangeLanded: 4.8,
    currentAsgFob: 12.9,
    newAsgFob: 13.4,
    currentDc: 16.1,
    marginStrategyType: "Blue",
    proposedDcGold: 17.2,
    proposedDcBlue: 16.7,
    proposedDcRed: 16.4,
    modifyMarginThreshold: 1.4,
    modifiedProposedDc: 16.9,
    modifyStrategy: "Blue launch",
    flag: false,
    flaggedReason: "",
    onHold: false,
    onHoldReason: "",
  },
  {
    id: "SKU-3002",
    parsedSku: "SKU-3002",
    hqLine: "H2",
    apgVendorNumber: "APG-9902",
    napaSkuNumber: "NS-77313",
    vendorDescription: "New items vendor",
    itemDescription: "Accessory pack",
    mfgPartNumber: "MP-3002",
    status: "Initial",
    category: "New items",
    demand: 760,
    percentDemand: 22,
    oldFobUnit: 10.8,
    oldFob: 8208,
    newLandingCost: 11.3,
    percentChangeLanded: 4.6,
    currentAsgFob: 11.0,
    newAsgFob: 11.5,
    currentDc: 14.8,
    marginStrategyType: "Blue",
    proposedDcGold: 15.9,
    proposedDcBlue: 15.3,
    proposedDcRed: 15.0,
    modifyMarginThreshold: 1.2,
    modifiedProposedDc: 15.4,
    modifyStrategy: "Blue review",
    flag: true,
    flaggedReason: "New item risk review",
    onHold: false,
    onHoldReason: "",
  },
];

void PRIORITY_DETAIL_ROWS;
void REGULAR_DETAIL_ROWS;
void NEW_DETAIL_ROWS;

export const getGroupedColumnSpecs = (batchId) => {
  const groupedSpecs = [
    {
      key: "vendorInfo",
      label: "Vendor info",
      fields: ["apgVendorNumber", "napaSkuNumber", "vendorDescription"],
      width: 180,
      summary: (row) => `${row.apgVendorNumber || "-"} / ${row.napaSkuNumber || "-"} / ${row.vendorDescription || "-"}`,
    },
  ];

  groupedSpecs.push(
    {
      key: "costInfo",
      label: "Cost info",
      fields: ["oldFobUnit", "oldFob", "oldLandedCost", "newLandingCost"],
      width: 200,
      summary: (row) =>
        `${formatFobUnit(row.oldFobUnit)} / ${currencyFormatter(row.oldFob)} / ${currencyFormatter(row.oldLandedCost)} / ${currencyFormatter(row.newLandingCost)}`,
    },
    {
      key: "dcInfo",
      label: "DC info",
      fields: ["currentAsgFob", "newAsgFob"],
      width: 160,
      summary: (row) => `${currencyFormatter(row.currentAsgFob)} / ${currencyFormatter(row.newAsgFob)}`,
    },
    {
      key: "dcMarginInfo",
      label: "Proposed DC, margin & strategy",
      fields: [
        "currentDc",
        "proposedDc",
        "oldMargin",
        "newMargin",
        "oldMarginPercent",
        "newMarginPercent",
        "percentChangeMargin",
        "modifyMarginThreshold",
        "marginStrategyType",
      ],
      width: 320,
      summary: (row) =>
        `${currencyFormatter(row.currentDc)} / ${currencyFormatter(row.proposedDc)} / ${currencyFormatter(row.oldMargin)} / ${currencyFormatter(row.newMargin)} / ${percentFormatter(row.percentChangeMargin)} / ${percentFormatter(row.modifyMarginThreshold)} / ${formatMarginStrategyType(row.marginStrategyType)}`,
    }
  );

  groupedSpecs.push({
    key: "modifyValuesInfo",
    label: "Modify values",
    fields: [
      "modifyMarginStrategyType",
      "modifyNewMarginPercent",
      "modifyNewMargin",
      "modifyProposedDc",
      "modifyNewDc",
    ],
    width: 320,
    summary: (row) =>
      `${formatMarginStrategyType(row.modifyMarginStrategyType)} / ${percentFormatter(row.modifyNewMarginPercent)} / ${currencyFormatter(row.modifyNewMargin)} / ${currencyFormatter(row.modifyProposedDc)} / ${percentFormatter(row.modifyNewDc)}`,
  });

  groupedSpecs.push(
    {
      key: "goldTier",
      label: "Gold tier",
      fields: [
        "currentDcGold",
        "newGoldPrice",
        "goldDc",
        "oldGoldMarginPercentage",
        "newGoldMarginPercentage",
        "goldMarginPercentageChange",
      ],
      width: 200,
      summary: (row) => formatTierSummary(row, "gold", { includeCurrentPrice: true }),
    },
    {
      key: "blueTier",
      label: "Blue tier",
      fields: [
        "currentDcBlue",
        "newBluePrice",
        "blueDc",
        "oldBlueMarginPercentage",
        "newBlueMarginPercentage",
        "blueMarginPercentageChange",
      ],
      width: 200,
      summary: (row) => formatTierSummary(row, "blue", { includeCurrentPrice: true }),
    },
    {
      key: "redTier",
      label: "Red tier",
      fields: [
        "currentDcRed",
        "newRedPrice",
        "redDc",
        "oldRedMarginPercentage",
        "newRedMarginPercentage",
        "redMarginPercentageChange",
      ],
      width: 200,
      summary: (row) => formatTierSummary(row, "red", { includeCurrentPrice: true }),
    }
  );

  return groupedSpecs;
};

const createTierCurrencyColumn = ({ field, headerName, tier, metric, minWidth }) => ({
  field,
  headerName,
  flex: 1,
  minWidth,
  valueFormatter: ({ row, value }) => currencyFormatter(value ?? getTierMetricValue(row, tier, metric)),
});

const createTierPercentColumn = ({ field, headerName, tier, metric, minWidth }) => ({
  field,
  headerName,
  flex: 1,
  minWidth,
  valueFormatter: ({ row, value }) => percentFormatter(value ?? getTierMetricValue(row, tier, metric)),
});

const getMarginColumns = ({ isThresholdEditing = false } = {}) => [
  {
    field: "oldMargin",
    headerName: "Old Margin $",
    flex: 0.9,
    minWidth: 130,
    valueFormatter: ({ value }) => currencyFormatter(value),
  },
  {
    field: "newMargin",
    headerName: "New Margin $",
    flex: 0.9,
    minWidth: 130,
    editable: false,
    type: "number",
    valueParser: (value) => normalizeThresholdValue(value),
    valueFormatter: ({ value }) => currencyFormatter(value),
  },
  {
    field: "oldMarginPercent",
    headerName: "Old Margin %",
    flex: 0.9,
    minWidth: 130,
    valueFormatter: ({ value }) => percentFormatter(value),
  },
  {
    field: "newMarginPercent",
    headerName: "New Margin %",
    flex: 0.9,
    minWidth: 130,
    editable: false,
    type: "number",
    valueParser: (value) => normalizeThresholdValue(value),
    valueFormatter: ({ value }) => percentFormatter(value),
  },
  {
    field: "percentChangeMargin",
    headerName: "% change in margin",
    flex: 1,
    minWidth: 150,
    valueFormatter: ({ value }) => percentFormatter(value),
  },
];

const getModifyColumns = ({ isThresholdEditing = false, onRowUpdate, setGridRows } = {}) => [
  {
    field: "modifyMarginStrategyType",
    headerName: "Modify margin strategy type",
    flex: 1.2,
    minWidth: 200,
    editable: isThresholdEditing,
    ...getThresholdEditableColumnClasses("modifyMarginStrategyType", isThresholdEditing),
    renderCell: ({ row, value }) =>
      isThresholdEditing ? (
        <Select
          value={value || ""}
          onChange={(event) => {
            const nextValue = event.target.value;
            const updatedRow = { ...row, modifyMarginStrategyType: nextValue };

            if (typeof onRowUpdate === "function") {
              onRowUpdate(updatedRow);
              return;
            }

            setGridRows((previousRows) =>
              previousRows.map((currentRow) => (currentRow.id === row.id ? updatedRow : currentRow))
            );
          }}
          variant="standard"
          disableUnderline
          fullWidth
          size="small"
          sx={{ fontSize: 13 }}
          renderValue={(selectedValue) => formatMarginStrategyType(selectedValue)}
        >
          {MARGIN_STRATEGY_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Typography variant="body2">{formatMarginStrategyType(value)}</Typography>
      ),
  },
  {
    field: "modifyNewMarginPercent",
    headerName: "Modify new margin %",
    flex: 1,
    minWidth: 150,
    editable: isThresholdEditing,
    type: "number",
    valueParser: (value) => normalizeThresholdValue(value),
    valueFormatter: ({ row = {}, value }) => percentFormatter(value ?? row.modifyNewMarginPercent),
    ...getThresholdEditableColumnClasses("modifyNewMarginPercent", isThresholdEditing),
  },
  {
    field: "modifyNewMargin",
    headerName: "Modify new margin $",
    flex: 1,
    minWidth: 150,
    editable: isThresholdEditing,
    type: "number",
    valueParser: (value) => normalizeThresholdValue(value),
    valueFormatter: ({ row = {}, value }) => currencyFormatter(value ?? row.modifyNewMargin),
    ...getThresholdEditableColumnClasses("modifyNewMargin", isThresholdEditing),
  },
  {
    field: "modifyProposedDc",
    headerName: "Modify proposed dc",
    flex: 1,
    minWidth: 150,
    editable: isThresholdEditing,
    type: "number",
    valueParser: (value) => normalizeThresholdValue(value),
    valueFormatter: ({ row = {}, value }) => currencyFormatter(value ?? row.modifyProposedDc),
    ...getThresholdEditableColumnClasses("modifyProposedDc", isThresholdEditing),
  },
  {
    field: "modifyNewDc",
    headerName: "Modify new dc %",
    flex: 0.9,
    minWidth: 130,
    editable: isThresholdEditing,
    type: "number",
    valueParser: (value) => normalizeThresholdValue(value),
    valueFormatter: ({ row = {}, value }) => percentFormatter(value ?? row.modifyNewDc),
    ...getThresholdEditableColumnClasses("modifyNewDc", isThresholdEditing),
  },
];

export const createDetailColumns = ({ setGridRows, batchId, isThresholdEditing = false, onRowUpdate }) => {
  const columns = [
  {
    field: "parsedSku",
    headerName: "parsedSku",
    width: 120,
    sortable: true,
    filterable: true,
  },
  {
    field: "hqLine",
    headerName: "hqLine",
    width: 90,
    sortable: true,
    filterable: true,
  },
  {
    field: "fieldLine",
    headerName: "fieldLine",
    width: 110,
    sortable: true,
    filterable: true,
  },
  { field: "status", headerName: "Status", flex: 0.8, minWidth: 110 },
  { field: "category", headerName: "Category", flex: 0.8, minWidth: 110 },
  { field: "demand", headerName: "Demand", flex: 0.8, minWidth: 110 },
  {
    field: "percentDemand",
    headerName: "% of demand",
    flex: 0.9,
    minWidth: 130,
    valueFormatter: ({ value }) => percentFormatter(value),
  },
  {
    field: "percentChangeLanded",
    headerName: "% change in landed",
    flex: 1,
    minWidth: 150,
    valueFormatter: ({ value }) => percentFormatter(value),
  },
  {
    field: "currentDc",
    headerName: "Current DC",
    flex: 1,
    minWidth: 140,
    valueFormatter: ({ value }) => currencyFormatter(value),
  },
  {
    field: "proposedDc",
    headerName: "Proposed DC",
    flex: 1,
    minWidth: 140,
    editable: false,
    type: "number",
    valueParser: (value) => normalizeThresholdValue(value),
    valueFormatter: ({ value }) => currencyFormatter(value),
  },
  ...getMarginColumns({ isThresholdEditing }),
  {
    field: "modifyMarginThreshold",
    headerName: "New DC %",
    flex: 0.9,
    minWidth: 120,
    editable: false,
    type: "number",
    valueParser: (value) => normalizeThresholdValue(value),
    valueFormatter: ({ value }) => percentFormatter(value),
  },
  {
    field: "marginStrategyType",
    headerName: "Margin strategy type",
    flex: 1.1,
    minWidth: 170,
    editable: false,
    renderCell: ({ value }) => <Typography variant="body2">{formatMarginStrategyType(value)}</Typography>,
  },
  ...getModifyColumns({ isThresholdEditing, onRowUpdate, setGridRows }),
  {
    field: "currentDcGold",
    headerName: "Current gold tier price",
    flex: 1,
    minWidth: 175,
    valueFormatter: ({ row, value }) => currencyFormatter(value ?? getTierPriceValue(row, "gold")),
  },
  {
    field: "currentDcBlue",
    headerName: "Current blue tier price",
    flex: 1,
    minWidth: 175,
    valueFormatter: ({ row, value }) => currencyFormatter(value ?? getTierPriceValue(row, "blue")),
  },
  {
    field: "currentDcRed",
    headerName: "Current red tier price",
    flex: 1,
    minWidth: 170,
    valueFormatter: ({ row, value }) => currencyFormatter(value ?? getTierPriceValue(row, "red")),
  },
  createTierCurrencyColumn({
    field: "goldDc",
    headerName: "Gold DC",
    tier: "gold",
    metric: "dc",
    minWidth: 120,
  }),
  createTierPercentColumn({
    field: "oldGoldMarginPercentage",
    headerName: "Old gold margin %",
    tier: "gold",
    metric: "oldMargin",
    minWidth: 130,
  }),
  createTierPercentColumn({
    field: "newGoldMarginPercentage",
    headerName: "New gold margin %",
    tier: "gold",
    metric: "newMargin",
    minWidth: 130,
  }),
  createTierPercentColumn({
    field: "goldMarginPercentageChange",
    headerName: "Gold margin % change",
    tier: "gold",
    metric: "marginChange",
    minWidth: 150,
  }),
  createTierCurrencyColumn({
    field: "blueDc",
    headerName: "Blue DC",
    tier: "blue",
    metric: "dc",
    minWidth: 120,
  }),
  createTierPercentColumn({
    field: "oldBlueMarginPercentage",
    headerName: "Old blue margin %",
    tier: "blue",
    metric: "oldMargin",
    minWidth: 130,
  }),
  createTierPercentColumn({
    field: "newBlueMarginPercentage",
    headerName: "New blue margin %",
    tier: "blue",
    metric: "newMargin",
    minWidth: 130,
  }),
  createTierPercentColumn({
    field: "blueMarginPercentageChange",
    headerName: "Blue margin % change",
    tier: "blue",
    metric: "marginChange",
    minWidth: 150,
  }),
  createTierCurrencyColumn({
    field: "redDc",
    headerName: "Red DC",
    tier: "red",
    metric: "dc",
    minWidth: 120,
  }),
  createTierPercentColumn({
    field: "oldRedMarginPercentage",
    headerName: "Old red margin %",
    tier: "red",
    metric: "oldMargin",
    minWidth: 130,
  }),
  createTierPercentColumn({
    field: "newRedMarginPercentage",
    headerName: "New red margin %",
    tier: "red",
    metric: "newMargin",
    minWidth: 130,
  }),
  createTierPercentColumn({
    field: "redMarginPercentageChange",
    headerName: "Red margin % change",
    tier: "red",
    metric: "marginChange",
    minWidth: 150,
  }),
  {
    field: "currentAsgFob",
    headerName: "Current ASG FOB",
    flex: 1,
    minWidth: 150,
    valueFormatter: ({ value }) => currencyFormatter(value),
  },
  {
    field: "newAsgFob",
    headerName: "New ASG FOB",
    flex: 1,
    minWidth: 140,
    valueFormatter: ({ value }) => currencyFormatter(value),
  },
  {
    field: "oldFobUnit",
    headerName: "oldFobUnit",
    flex: 0.9,
    minWidth: 130,
    valueFormatter: ({ value }) => formatFobUnit(value),
  },
  {
    field: "oldFob",
    headerName: "Old FOB",
    flex: 0.9,
    minWidth: 120,
    valueFormatter: ({ value }) => currencyFormatter(value),
  },
  {
    field: "oldLandedCost",
    headerName: "Old landed cost",
    flex: 1,
    minWidth: 150,
    valueFormatter: ({ value }) => currencyFormatter(value),
  },
  {
    field: "newLandingCost",
    headerName: "New landing cost",
    flex: 1,
    minWidth: 140,
    valueFormatter: ({ value }) => currencyFormatter(value),
  },
  {
    field: "apgVendorNumber",
    headerName: "ASG Vendor #",
    flex: 1,
    minWidth: 140,
  },
  {
    field: "napaSkuNumber",
    headerName: "NAPA SKU #",
    flex: 1,
    minWidth: 140,
  },
  {
    field: "vendorDescription",
    headerName: "Vendor Name",
    flex: 1.2,
    minWidth: 170,
  },
  {
    field: "itemDescription",
    headerName: "Item description",
    flex: 1.2,
    minWidth: 170,
  },
  {
    field: "mfgPartNumber",
    headerName: "MFG part #",
    flex: 1,
    minWidth: 140,
  },
  { field: "flag", headerName: "Flag", flex: 0.6, minWidth: 90 },
  { field: "onHold", headerName: "On hold", flex: 0.7, minWidth: 110 },
  ];

  return columns;
};

export const getExportColumns = (detailColumns = []) =>
  detailColumns.filter((column) => !["actions", "flaggedReason", "onHoldReason"].includes(column.field));

export const getVisibleExportColumns = (frozenColumns = [], detailColumns = []) => {
  const seenFields = new Set();

  return [...(Array.isArray(frozenColumns) ? frozenColumns : []), ...(Array.isArray(detailColumns) ? detailColumns : [])]
    .filter((column) => !["actions", "flaggedReason", "onHoldReason"].includes(column.field))
    .filter((column) => {
      if (!column?.field || seenFields.has(column.field)) {
        return false;
      }

      seenFields.add(column.field);
      return true;
    });
};

export const getDefaultExportFileName = (batchId = null) =>
  batchId ? `DC Cost Detail for Batch ${batchId} export.xlsx` : "dc cost flow details.xlsx";

const DCCostBatchDetail = ({
  batchIdOverride = null,
  detailRowsOverride = null,
  rowActionsOverride = null,
  onRestoreSuccess = null,
  hideHeader = false,
  hideDescription = false,
  hideBulkActions = false,
  showExportButton = true,
  hideEmptyStateMessage = false,
  frozenColumnsOverride = null,
  titleOverride = null,
  noteOverride = null,
  exportFileNameOverride = null,
  onDataRefresh = null,
  itemLevelActionsDisabled = true,
} = {}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const batchId = batchIdOverride ?? searchParams.get("batchId");
  const apiRef = useGridApiRef();
  const [selectionModel, setSelectionModel] = useState([]);
  const [gridRows, setGridRows] = useState([]);
  const [detailGridResetKey, setDetailGridResetKey] = useState(0);
  const [loadedDetailRows, setLoadedDetailRows] = useState([]);
  const [detailRowsLoading, setDetailRowsLoading] = useState(false);
  const [detailRowsError, setDetailRowsError] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [totalDetailRowCount, setTotalDetailRowCount] = useState(0);
  const [totalDetailPageCount, setTotalDetailPageCount] = useState(0);
  const [sortModel, setSortModel] = useState([]);
  const [frozenFilters, setFrozenFilters] = useState({ parsedSku: "", batchId: "", hqLine: "" });
  const [frozenMenuAnchorEl, setFrozenMenuAnchorEl] = useState(null);
  const [frozenMenuField, setFrozenMenuField] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, type: null, row: null });
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approveAction, setApproveAction] = useState(null);
  const [isApproving, setIsApproving] = useState(false);
  const [approvalValueSource, setApprovalValueSource] = useState(APPROVAL_VALUE_SOURCES.PROPOSED);
  const [expandedGroups, setExpandedGroups] = useState([]);
  const [isThresholdEditing, setIsThresholdEditing] = useState(false);
  const [isThresholdSaving, setIsThresholdSaving] = useState(false);
  const thresholdSnapshotRef = useRef([]);
  const dirtyThresholdRowsRef = useRef(new Map());
  const isMountedRef = useRef(true);
  const detailRowsRequestRef = useRef(0);

  const openRowAction = useCallback((type, row) => {
    if (itemLevelActionsDisabled) {
      return;
    }

    setActionDialog({ open: true, type, row });
  }, [itemLevelActionsDisabled]);

  const closeRowAction = useCallback(() => {
    setActionDialog({ open: false, type: null, row: null });
  }, []);

  const loadDetailRows = useCallback(async ({ pageIndex = 0, nextPageSize } = {}) => {
    const effectivePageSize = Number.isFinite(Number(nextPageSize)) ? Number(nextPageSize) : 100;

    if (Array.isArray(detailRowsOverride)) {
      if (isMountedRef.current) {
        setLoadedDetailRows(detailRowsOverride.map((row, index) => normalizeDetailRow(row, index)));
        setTotalDetailRowCount(detailRowsOverride.length);
        setTotalDetailPageCount(detailRowsOverride.length ? Math.max(1, Math.ceil(detailRowsOverride.length / effectivePageSize)) : 0);
        setDetailRowsError(null);
        setDetailRowsLoading(false);
      }
      return;
    }

    if (detailRowsOverride) {
      const resolvedOverrideRows = extractDetailRows(detailRowsOverride);

      if (isMountedRef.current) {
        setLoadedDetailRows(resolvedOverrideRows.map((row, index) => normalizeDetailRow(row, index)));
        setTotalDetailRowCount(resolvedOverrideRows.length);
        setTotalDetailPageCount(resolvedOverrideRows.length ? Math.max(1, Math.ceil(resolvedOverrideRows.length / effectivePageSize)) : 0);
        setDetailRowsError(null);
        setDetailRowsLoading(false);
      }

      return;
    }

    if (!batchId) {
      if (isMountedRef.current) {
        setLoadedDetailRows([]);
        setTotalDetailRowCount(0);
        setTotalDetailPageCount(0);
        setDetailRowsError(null);
        setDetailRowsLoading(false);
      }
      return;
    }

    const requestId = ++detailRowsRequestRef.current;

    if (isMountedRef.current) {
      setLoadedDetailRows([]);
      setDetailRowsLoading(true);
      setDetailRowsError(null);
    }

    try {
      const rows = await getDCCostItemsRows(batchId, { page: pageIndex, size: effectivePageSize });
      const resolvedRows = extractDetailRows(rows);

      if (isMountedRef.current && detailRowsRequestRef.current === requestId) {
        setLoadedDetailRows(resolvedRows.map((row, index) => normalizeDetailRow(row, index)));
        setTotalDetailRowCount(Number(rows?.totalElements) || resolvedRows.length);
        setTotalDetailPageCount(Number(rows?.totalPages) || (resolvedRows.length ? Math.max(1, Math.ceil((Number(rows?.totalElements) || resolvedRows.length) / effectivePageSize)) : 0));
        if (Number.isFinite(Number(rows?.pageNumber))) {
          setPage(Number(rows.pageNumber));
        } else {
          setPage(pageIndex);
        }
        if (Number.isFinite(Number(rows?.pageSize))) {
          setPageSize(Number(rows.pageSize));
        } else {
          setPageSize(effectivePageSize);
        }
      }
    } catch (error) {
      if (isMountedRef.current && detailRowsRequestRef.current === requestId) {
        setLoadedDetailRows([]);
        setTotalDetailRowCount(0);
        setTotalDetailPageCount(0);
        setDetailRowsError(error?.message || "Failed to load DC cost detail data.");
      }
    } finally {
      if (isMountedRef.current && detailRowsRequestRef.current === requestId) {
        setDetailRowsLoading(false);
      }
    }
  }, [batchId, detailRowsOverride]);

  const confirmRowAction = useCallback(async () => {
    if (!actionDialog.row || !actionDialog.type) {
      return;
    }

    const shouldRestore = actionDialog.type === "Unblock" || actionDialog.type === "Remove flag";
    const response = shouldRestore
      ? await restoreSkuStatus(actionDialog.row, actionDialog.type)
      : await updateSkuStatus(actionDialog.row, actionDialog.type);
    if (response) {
      if (shouldRestore) {
        await loadDetailRows({ pageIndex: page, nextPageSize: pageSize });
        if (typeof onRestoreSuccess === "function") {
          void onRestoreSuccess(actionDialog.row, actionDialog.type);
        }
      } else if (!shouldRestore) {
        void loadDetailRows({ pageIndex: page, nextPageSize: pageSize });
        if (typeof onDataRefresh === "function") {
          await onDataRefresh();
        }
      }
      closeRowAction();
    }
  }, [actionDialog, closeRowAction, loadDetailRows, onDataRefresh, onRestoreSuccess, page, pageSize]);

  const beginThresholdEdit = useCallback(() => {
    if (itemLevelActionsDisabled) {
      return;
    }

    thresholdSnapshotRef.current = gridRows;
    dirtyThresholdRowsRef.current.clear();
    setIsThresholdEditing(true);
  }, [gridRows, itemLevelActionsDisabled]);

  const resetThresholdEditUi = useCallback((snapshotRows = thresholdSnapshotRef.current) => {
    const editRowsModel = apiRef.current?.getEditRowsModel?.() || apiRef.current?.state?.editRows || {};
    Object.entries(editRowsModel).forEach(([rowId, fields]) => {
      Object.keys(fields || {}).forEach((field) => {
        void apiRef.current?.stopCellEditMode?.({
          id: rowId,
          field,
          ignoreModifications: true,
        });
      });
    });

    setGridRows(Array.isArray(snapshotRows) ? [...snapshotRows] : []);
    dirtyThresholdRowsRef.current.clear();
    setIsThresholdEditing(false);
    setDetailGridResetKey((current) => current + 1);
  }, [apiRef]);

  const cancelThresholdEdit = useCallback(() => {
    resetThresholdEditUi();
  }, [resetThresholdEditUi]);

  const submitThresholdEdit = useCallback(async () => {
    const dirtyRows = Array.from(dirtyThresholdRowsRef.current.values());

    if (!dirtyRows.length) {
      AlertManager.warning("Make a threshold change before submitting.", { contactSupport: false });
      return;
    }

    const invalidRow = dirtyRows.find((row) => !canModifyThresholdRow(row));
    if (invalidRow) {
      AlertManager.warning(
        `Item has status '${invalidRow.status ?? invalidRow.repricingStatus ?? ""}'. Only items with status ESTIMATED can be modified.`,
        { contactSupport: false }
      );
      return;
    }

    const payloads = buildThresholdUpdatePayload(
      dirtyRows,
      thresholdSnapshotRef.current,
      batchId
    );
    if (!payloads.length) {
      AlertManager.warning("Make a threshold change before submitting.", { contactSupport: false });
      return;
    }

    setIsThresholdSaving(true);

    try {
      const response = await modifyDCCostThreshold({
        batchId,
        rows: dirtyRows,
        originalRows: thresholdSnapshotRef.current,
      });
      if (!response) {
        return;
      }

      const responseRows = Array.isArray(response) ? response : [response];
      const updatedRows = applyModifyThresholdResponses(gridRows, responseRows);
      resetThresholdEditUi(updatedRows);
      thresholdSnapshotRef.current = updatedRows;
      if (typeof onDataRefresh === "function") {
        await onDataRefresh();
      }
    } finally {
      setIsThresholdSaving(false);
    }
  }, [batchId, gridRows, onDataRefresh, resetThresholdEditUi]);

  const handleThresholdButtonClick = useCallback(() => {
    if (itemLevelActionsDisabled) {
      return;
    }

    if (!isThresholdEditing) {
      beginThresholdEdit();
      return;
    }

    void submitThresholdEdit();
  }, [beginThresholdEdit, isThresholdEditing, itemLevelActionsDisabled, submitThresholdEdit]);

  const detailRows = useMemo(() => {
    if (Array.isArray(detailRowsOverride)) {
      return detailRowsOverride.map((row, index) => normalizeDetailRow(row, index));
    }

    if (detailRowsOverride) {
      return extractDetailRows(detailRowsOverride).map((row, index) => normalizeDetailRow(row, index));
    }

    return loadedDetailRows;
  }, [detailRowsOverride, loadedDetailRows]);

  const isServerPagedDetailRows = Boolean(batchId) && !detailRowsOverride;

  useEffect(() => {
    isMountedRef.current = true;

    void loadDetailRows({ pageIndex: page, nextPageSize: pageSize });

    return () => {
      isMountedRef.current = false;
      detailRowsRequestRef.current += 1;
    };
  }, [loadDetailRows, page, pageSize]);

  const handleThresholdRowUpdate = useCallback((updatedRow) => {
    if (!canModifyThresholdRow(updatedRow)) {
      AlertManager.warning("Only items with status ESTIMATED can be modified.", { contactSupport: false });
      return updatedRow;
    }

    const normalizedRow = {
      ...updatedRow,
      modifyProposedDc: normalizeThresholdValue(updatedRow.modifyProposedDc ?? updatedRow.proposedDc),
      modifyNewMargin: normalizeThresholdValue(updatedRow.modifyNewMargin ?? updatedRow.newMargin),
      modifyNewMarginPercent: normalizeThresholdValue(updatedRow.modifyNewMarginPercent ?? updatedRow.newMarginPercent),
      modifyNewDc: normalizeThresholdValue(updatedRow.modifyNewDc ?? updatedRow.modifyMarginThreshold),
      modifyMarginStrategyType: updatedRow.modifyMarginStrategyType ?? updatedRow.marginStrategyType,
      modifyStrategy: updatedRow.modifyStrategy ?? updatedRow.strategy ?? "",
    };

    setGridRows((currentRows) => currentRows.map((row) => (row.id === normalizedRow.id ? normalizedRow : row)));
    dirtyThresholdRowsRef.current.set(normalizedRow.id, normalizedRow);
    return normalizedRow;
  }, []);

  const detailColumns = useMemo(
    () => createDetailColumns({ setGridRows, batchId, isThresholdEditing, onRowUpdate: handleThresholdRowUpdate }),
    [batchId, handleThresholdRowUpdate, isThresholdEditing, setGridRows]
  );

  const isGroupedView = Boolean(batchId || (Array.isArray(detailRowsOverride) && detailRowsOverride.length));

  const toggleGroup = useCallback((groupKey) => {
    setExpandedGroups((currentGroups) =>
      currentGroups.includes(groupKey)
        ? currentGroups.filter((currentGroup) => currentGroup !== groupKey)
        : [...currentGroups, groupKey]
    );
  }, []);

  useEffect(() => {
    setGridRows(detailRows);
    setSelectionModel([]);
    if (!isServerPagedDetailRows) {
      setPage(0);
    }
    setSortModel([]);
    setFrozenFilters({ parsedSku: "", hqLine: "" });
    setFrozenMenuAnchorEl(null);
    setFrozenMenuField(null);
    setExpandedGroups([]);
    dirtyThresholdRowsRef.current.clear();
    setIsThresholdEditing(false);
  }, [detailRows, isServerPagedDetailRows]);

  const filteredGridRows = useMemo(
    () => filterDCCostRowsByFrozenColumns(gridRows, frozenFilters),
    [frozenFilters, gridRows]
  );

  const sortedGridRows = useMemo(
    () => sortDCCostRowsByModel(filteredGridRows, sortModel),
    [filteredGridRows, sortModel]
  );

  const visibleRows = useMemo(
    () => (isServerPagedDetailRows ? sortedGridRows : sortedGridRows.slice(page * pageSize, page * pageSize + pageSize)),
    [isServerPagedDetailRows, page, pageSize, sortedGridRows]
  );

  const pageCount = useMemo(() => {
    if (totalDetailPageCount) {
      return totalDetailPageCount;
    }

    const totalRows = totalDetailRowCount || sortedGridRows.length;
    return totalRows ? Math.max(1, Math.ceil(totalRows / pageSize)) : 0;
  }, [pageSize, sortedGridRows.length, totalDetailPageCount, totalDetailRowCount]);

  const CustomPagination = useCallback(() => {
    const handleRowsPerPageChange = (event) => {
      const nextRowsPerPage = Number(event.target.value);

      if (!Number.isFinite(nextRowsPerPage)) {
        return;
      }

      setPageSize(nextRowsPerPage);
      setPage(0);
    };

    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
            Rows per page
          </Typography>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <Select
              value={String(pageSize)}
              onChange={handleRowsPerPageChange}
              displayEmpty
              renderValue={(value) => value}
              inputProps={{ "aria-label": "Rows per page" }}
              data-testid="detail-rows-per-page-select"
            >
              {DETAIL_ROWS_PER_PAGE_OPTIONS.map((option) => (
                <MenuItem key={option} value={String(option)}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Pagination
          color="primary"
          count={pageCount || 1}
          page={(page || 0) + 1}
          onChange={(_, value) => setPage(value - 1)}
        />
      </Box>
    );
  }, [page, pageCount, pageSize]);

  const CustomFooter = () => (
    <GridFooterContainer>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          py: 1.5,
          px: 2,
        }}
      >
        <Typography variant="body2" sx={{ whiteSpace: "nowrap", color: "#4b5563" }}>
          Total elements: {totalDetailRowCount.toLocaleString()}
        </Typography>
        <CustomPagination />
      </Box>
    </GridFooterContainer>
  );

  const frozenRowActions = useMemo(
    () =>
    rowActionsOverride ?? [
        { type: "Unblock", title: "Unblock", icon: <CheckCircleOutlineIcon fontSize="inherit" /> },
        { type: "Block", title: "Block", icon: <BlockIcon fontSize="inherit" /> },
        { type: "Flag", title: "Flag", icon: <DoNotDisturbOffOutlinedIcon fontSize="inherit" /> },
      ],
    [rowActionsOverride]
  );

  const canBlockOrFlagRow = useCallback((row) => canModifyThresholdRow(row), []);
  const canRestoreRow = useCallback((row) => normalizeRowStatus(row) === "BLOCKED", []);

  const frozenColumns = useMemo(
    () =>
      frozenColumnsOverride || [
        { field: "parsedSku", headerName: "parsedSku", width: 120 },
        { field: "hqLine", headerName: "hqLine", width: 90 },
      ],
    [frozenColumnsOverride]
  );

  const frozenActionColumnWidth = frozenRowActions.length > 1 ? 96 : 48;
  const frozenHeaderGridTemplateColumns =
    frozenColumns.length === 3
      ? `36px ${frozenActionColumnWidth}px 120px 110px 90px`
      : `36px ${frozenActionColumnWidth}px 132px 110px`;
  const frozenRowGridTemplateColumns =
    frozenColumns.length === 3
      ? `36px ${frozenActionColumnWidth}px 120px 110px 90px`
      : `36px ${frozenActionColumnWidth}px 120px 90px`;
  const frozenPanelWidth =
    (frozenColumns.length === 3
      ? 36 + frozenActionColumnWidth + 120 + 110 + 90
      : 36 + frozenActionColumnWidth + 132 + 110) + 20;

  const frozenColumnSortDirection = useCallback(
    (field) => sortModel.find((sortItem) => sortItem.field === field)?.sort || null,
    [sortModel]
  );

  const handleFrozenFilterChange = useCallback((field, value) => {
    setFrozenFilters((currentFilters) => ({
      ...currentFilters,
      [field]: value,
    }));
    setPage(0);
  }, []);

  const handleFrozenSortToggle = useCallback(
    (field) => {
      const currentDirection = frozenColumnSortDirection(field);
      const nextDirection = currentDirection === "asc" ? "desc" : "asc";

      setSortModel([{ field, sort: nextDirection }]);
    },
    [frozenColumnSortDirection]
  );

  const handleFrozenColumnMenu = useCallback(
    (event, field) => {
      setFrozenMenuAnchorEl(event.currentTarget);
      setFrozenMenuField(field);
    },
    []
  );

  const closeFrozenColumnMenu = useCallback(() => {
    setFrozenMenuAnchorEl(null);
    setFrozenMenuField(null);
  }, []);

  const handleFrozenColumnMenuSort = useCallback(
    (direction) => {
      if (!frozenMenuField) {
        return;
      }

      setSortModel([{ field: frozenMenuField, sort: direction }]);
      closeFrozenColumnMenu();
    },
    [closeFrozenColumnMenu, frozenMenuField]
  );

  const clearFrozenColumnSort = useCallback(() => {
    setSortModel([]);
    closeFrozenColumnMenu();
  }, [closeFrozenColumnMenu]);

  const clearFrozenColumnFilter = useCallback(() => {
    if (!frozenMenuField) {
      return;
    }

    setFrozenFilters((currentFilters) => ({
      ...currentFilters,
      [frozenMenuField]: "",
    }));
    setPage(0);
    closeFrozenColumnMenu();
  }, [closeFrozenColumnMenu, frozenMenuField]);

  const scrollableColumns = useMemo(
    () => {
      const baseColumns = detailColumns;

      if (!isGroupedView) {
        return baseColumns;
      }

      const groupedColumnSpecs = getGroupedColumnSpecs(batchId);
      const groupedFields = new Set(groupedColumnSpecs.flatMap((spec) => spec.fields));
      const columnByField = new Map(baseColumns.map((column) => [column.field, column]));
      const renderedGroupKeys = new Set();
      const groupedColumns = [];

      baseColumns.forEach((column) => {
        const groupSpec = groupedColumnSpecs.find((spec) => spec.fields.includes(column.field));

        if (!groupSpec) {
          if (!groupedFields.has(column.field)) {
            groupedColumns.push(column);
          }

          return;
        }

        if (renderedGroupKeys.has(groupSpec.key)) {
          return;
        }

        renderedGroupKeys.add(groupSpec.key);

        const isExpanded = expandedGroups.includes(groupSpec.key);

        groupedColumns.push({
          field: `${groupSpec.key}Summary`,
          headerName: groupSpec.label,
          width: groupSpec.width,
          sortable: false,
          filterable: false,
          headerAlign: "left",
          renderHeader: () => (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, width: "100%" }}>
              <Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                {groupSpec.label}
              </Typography>
              <IconButton
                size="small"
                aria-label={`${isExpanded ? "Collapse" : "Expand"} ${groupSpec.label}`}
                title={isExpanded ? "Collapse" : "Expand"}
                onClick={(event) => {
                  event.stopPropagation();
                  toggleGroup(groupSpec.key);
                }}
                sx={{ color: "#001489", ml: "auto", p: 0.25 }}
              >
                {isExpanded ? <RemoveIcon fontSize="inherit" /> : <AddIcon fontSize="inherit" />}
              </IconButton>
            </Box>
          ),
          renderCell: ({ row }) => (
            <Typography
              variant="body2"
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                width: "100%",
                color: isExpanded ? "#6b7280" : "inherit",
              }}
            >
              {isExpanded ? groupSpec.label : groupSpec.summary(row)}
            </Typography>
          ),
        });

        if (expandedGroups.includes(groupSpec.key)) {
          groupSpec.fields.forEach((fieldName) => {
            const childColumn = columnByField.get(fieldName);
            if (childColumn) {
              groupedColumns.push(childColumn);
            }
          });
          return;
        }
      });

      return groupedColumns;
    },
    [batchId, detailColumns, expandedGroups, isGroupedView, toggleGroup]
  );

  const selectedRows = useMemo(
    () => gridRows.filter((row) => selectionModel.includes(row.id)),
    [gridRows, selectionModel]
  );

  const exportColumns = useMemo(
    () => getVisibleExportColumns(frozenColumns, detailColumns),
    [detailColumns, frozenColumns]
  );

  const handleExport = useCallback(() => {
    exportRowsToXlsx({
      columns: exportColumns,
      rows: gridRows,
      fileName: exportFileNameOverride || getDefaultExportFileName(batchId),
      sheetName: "DC Detail",
      warningMessage: "No DC cost detail data to export.",
    });
  }, [batchId, exportColumns, exportFileNameOverride, gridRows]);

  const exportButton = showExportButton ? (
    <Button
      size="small"
      variant="contained"
      startIcon={<DownloadIcon fontSize="small" />}
      onClick={handleExport}
      disabled={!gridRows.length}
      sx={{ textTransform: "none" }}
    >
      Export all
    </Button>
  ) : null;

  const handleApprove = useCallback(() => {
    if (itemLevelActionsDisabled) {
      return;
    }

    if (!selectedRows.length) {
      AlertManager.warning("Select one or more items to approve.", { contactSupport: false });
      return;
    }

    if (selectedRows.some((row) => !canApproveRow(row))) {
      AlertManager.warning("Only estimated or escalated items can be approved.", { contactSupport: false });
      return;
    }

    setApprovalValueSource(APPROVAL_VALUE_SOURCES.PROPOSED);
    setApproveDialogOpen(true);
  }, [itemLevelActionsDisabled, selectedRows]);

  const closeApproveDialog = useCallback(() => {
    if (isApproving) {
      return;
    }

    setApproveDialogOpen(false);
    setApproveAction(null);
    setApprovalValueSource(APPROVAL_VALUE_SOURCES.PROPOSED);
  }, [isApproving]);

  const confirmApproveAction = useCallback(
    async (decision) => {
      if (!selectedRows.length) {
        AlertManager.warning("Select one or more items to approve.", { contactSupport: false });
        return;
      }

      if (!decision) {
        closeApproveDialog();
        return;
      }

      setApproveAction(decision);
      setIsApproving(true);

      const comments = decision === "draft" ? "Need to review later" : "Approved selected items";
      const successMessage =
        decision === "draft" ? "Selected items saved as draft successfully." : "Selected items approved successfully.";
      try {
        const response =
          decision === "draft"
            ? await draftDCCostBatch(batchId, selectedRows, { comments, successMessage })
            : await approveDCCostBatch(batchId, selectedRows, {
                comments,
                successMessage,
                useModifiedValues: approvalValueSource === APPROVAL_VALUE_SOURCES.MODIFY,
              });
        if (response) {
          setSelectionModel([]);
          await loadDetailRows({ pageIndex: page, nextPageSize: pageSize });
          if (typeof onDataRefresh === "function") {
            await onDataRefresh();
          }
          setApproveDialogOpen(false);
          setApproveAction(null);
        }
      } finally {
        setIsApproving(false);
      }
    },
    [approvalValueSource, batchId, closeApproveDialog, loadDetailRows, onDataRefresh, page, pageSize, selectedRows]
  );

  const handleApproveDialogClose = useCallback(() => {
    if (!isApproving) {
      closeApproveDialog();
    }
  }, [closeApproveDialog, isApproving]);

  const handleReject = useCallback(async () => {
    if (itemLevelActionsDisabled) {
      return;
    }

    if (!selectedRows.length) {
      AlertManager.warning("Select one or more items to reject.", { contactSupport: false });
      return;
    }

    if (selectedRows.some((row) => !canApproveRow(row))) {
      AlertManager.warning("Only estimated or escalated items can be rejected.", { contactSupport: false });
      return;
    }

    const response = await rejectDCCostBatch(batchId, selectedRows);
    if (response) {
      setSelectionModel([]);
      await loadDetailRows({ pageIndex: page, nextPageSize: pageSize });
      if (typeof onDataRefresh === "function") {
        await onDataRefresh();
      }
    }
  }, [batchId, itemLevelActionsDisabled, loadDetailRows, onDataRefresh, page, pageSize, selectedRows]);

  const pageTitle = titleOverride || `DC Cost Detail ${batchId ? `for Batch ${batchId}` : ""}`;
  const pageNote =
    noteOverride ||
    "Use the checkboxes to select one or more items. Item-level modify, approve, reject, block, unblock, and flag actions are disabled on this page.";
  const bulkActions = !hideBulkActions ? (
    <div className={pageClasses.batchActions}>
      {exportButton}
      <Button
        size="small"
        variant="outlined"
        onClick={handleThresholdButtonClick}
        disabled={itemLevelActionsDisabled || isThresholdSaving || !gridRows.length}
      >
        {isThresholdEditing ? (isThresholdSaving ? "Submitting..." : "Submit threshold") : "Modify threshold"}
      </Button>
      {isThresholdEditing ? (
        <Button size="small" variant="text" onClick={cancelThresholdEdit} disabled={isThresholdSaving}>
          Cancel
        </Button>
      ) : null}
      <Button
        size="small"
        variant="contained"
        color="success"
        onClick={() => void handleApprove()}
        disabled={itemLevelActionsDisabled || !selectedRows.length || isThresholdEditing || selectedRows.some((row) => !canApproveRow(row))}
      >
        Approve
      </Button>
      <Button
        size="small"
        variant="contained"
        color="error"
        onClick={() => void handleReject()}
        disabled={itemLevelActionsDisabled || !selectedRows.length || isThresholdEditing || selectedRows.some((row) => !canApproveRow(row))}
      >
        Reject
      </Button>
    </div>
  ) : null;
  const standaloneExportAction = hideBulkActions ? exportButton : null;
  const headerActions = bulkActions || standaloneExportAction;

  return (
    <section
      className={resultsClasses.page}
      style={{
        height: "calc(100vh - 64px)",
        minHeight: 0,
        overflow: "hidden",
        padding: 12,
        gap: 12,
      }}
    >
      {!hideHeader ? (
        <header className={resultsClasses.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <IconButton
              size="small"
              aria-label="Back to approvals"
              onClick={() => navigate("/dc-cost/approvals")}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="h2" className={resultsClasses.sectionTitle}>
              {pageTitle}
            </Typography>
          </div>
          {headerActions}
        </header>
      ) : null}

      {hideHeader && headerActions ? (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 0.5 }}>{headerActions}</Box>
      ) : null}

      {!hideDescription ? (
        <Box sx={{ color: "#4b5563", fontSize: 14, mb: 0.5 }}>{pageNote}</Box>
      ) : null}

      {detailRowsLoading ? (
        <Box sx={{ color: "#4b5563", fontSize: 14, mb: 0.5 }}>Loading DC cost detail rows...</Box>
      ) : null}

      {detailRowsError ? (
        <Box sx={{ color: "#b91c1c", fontSize: 14, mb: 0.5 }}>{detailRowsError}</Box>
      ) : null}

      <div
        className={resultsClasses.content}
        style={{
          flex: 1,
          minHeight: 0,
        }}
      >
        <div
          className={tableClasses.resultsTableCard}
          style={{
            display: "flex",
            gap: 0,
            overflow: "auto",
            minHeight: 0,
            flex: 1,
          }}
        >
          <Box
            sx={{
              width: frozenPanelWidth,
              flexShrink: 0,
              borderRight: "1px solid #e5e7eb",
              backgroundColor: "#fff",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: frozenHeaderGridTemplateColumns,
                position: "sticky",
                top: 0,
                zIndex: 2,
                backgroundColor: "#fff",
                borderBottom: "1px solid #e5e7eb",
                height: 48,
                alignItems: "center",
                fontWeight: 500,
                fontSize: 14,
                px: 1,
              }}
            >
              <Box />
              <Box />
              {frozenColumns.map((column) => {
                const sortDirection = frozenColumnSortDirection(column.field);
                const SortIcon = sortDirection === "desc" ? ArrowDownwardIcon : ArrowUpwardIcon;

                return (
                  <Box key={column.field} sx={{ pr: 0.5, display: "flex", alignItems: "center", gap: 0.25 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1, color: "#111827", flex: 1 }}>
                      {column.headerName}
                    </Typography>
                    <IconButton
                      size="small"
                      title={`Sort ${column.headerName}`}
                      aria-label={`Sort ${column.headerName}`}
                      onClick={() => handleFrozenSortToggle(column.field)}
                      sx={{ p: 0.25, color: "#001489" }}
                    >
                      <SortIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton
                      size="small"
                      title={`Open ${column.headerName} menu`}
                      aria-label={`Open ${column.headerName} menu`}
                      onClick={(event) => handleFrozenColumnMenu(event, column.field)}
                      sx={{ p: 0.25, color: "#001489" }}
                    >
                      <MoreVertIcon fontSize="inherit" />
                    </IconButton>
                  </Box>
                );
              })}
            </Box>
            {visibleRows.map((row) => (
              <Box
                key={row.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: frozenRowGridTemplateColumns,
                  height: 40,
                  alignItems: "center",
                  borderBottom: "1px solid #f3f4f6",
                  px: 0.75,
                  fontSize: 13,
                }}
              >
                <Box />
                <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 0.25, width: "100%" }}>
                  {frozenRowActions.map((action) => (
                    <IconButton
                      key={action.type}
                      size="small"
                      title={action.title}
                      aria-label={`${action.title} ${row.parsedSku}`}
                      disabled={
                        itemLevelActionsDisabled ||
                        ((action.type === "Block" || action.type === "Flag") && !canBlockOrFlagRow(row)) ||
                        (action.type === "Unblock" && !canRestoreRow(row))
                      }
                      onClick={() => openRowAction(action.type, row)}
                      sx={{ color: "#001489", p: 0.25 }}
                    >
                      {action.icon}
                    </IconButton>
                  ))}
                </Box>
                <Box sx={{ pr: 0.25, fontWeight: 400 }}>{row.parsedSku}</Box>
                {frozenColumns.length === 3 ? <Box sx={{ pr: 0.25, fontWeight: 400 }}>{row.batchId || "-"}</Box> : null}
                <Box sx={{ pr: 0.5 }}>{row.hqLine}</Box>
              </Box>
            ))}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DataGrid
              key={`${batchId || "detail"}-${detailGridResetKey}`}
              apiRef={apiRef}
              autoHeight
              rows={visibleRows}
              columns={scrollableColumns}
              disableVirtualization
              editMode="cell"
              isCellEditable={({ row, field }) => isThresholdEditing && THRESHOLD_EDITABLE_FIELDS.has(field) && canModifyThresholdRow(row)}
              processRowUpdate={handleThresholdRowUpdate}
              onProcessRowUpdateError={(error) => console.error(error)}
              experimentalFeatures={{ newEditingApi: true }}
              checkboxSelection
              isRowSelectable={({ row }) => canApproveRow(row)}
              disableRowSelectionOnClick
              sortModel={sortModel}
              onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
              headerHeight={48}
              rowHeight={40}
              getRowId={(row) => row.id}
              hideFooterSelectedRowCount
              selectionModel={selectionModel}
              onSelectionModelChange={(newSelectionModel) => setSelectionModel(newSelectionModel)}
              loading={detailRowsLoading || isThresholdSaving}
              components={{ Footer: CustomFooter }}
              columnVisibilityModel={{ parsedSku: false, hqLine: false }}
              sx={{
                "& .MuiDataGrid-cell": {
                  px: 0.75,
                  py: 0.5,
                },
                "& .MuiDataGrid-columnHeader": {
                  px: 0.75,
                },
                "& .MuiDataGrid-columnHeaderTitleContainer": {
                  px: 0.5,
                },
                "& .threshold-editable-column-header": {
                  bgcolor: "rgba(0, 20, 137, 0.12)",
                  color: "#001489",
                  fontWeight: 700,
                },
                "& .threshold-editable-column-cell": {
                  bgcolor: "rgba(0, 20, 137, 0.08)",
                  color: "#001489",
                },
                "& .threshold-editable-column-cell.MuiDataGrid-cell--editing": {
                  bgcolor: "rgba(0, 20, 137, 0.16)",
                },
                "& .threshold-editable-column-cell .MuiInputBase-root": {
                  color: "#001489",
                },
                "& .threshold-editable-column-cell.MuiDataGrid-cell--editing .MuiInputBase-root": {
                  backgroundColor: "#fff",
                  borderRadius: 1,
                  px: 1,
                },
                "& .threshold-editable-column-cell.MuiDataGrid-cell--editing .MuiInputBase-input": {
                  backgroundColor: "#fff",
                },
                "& .threshold-editable-column-cell.MuiDataGrid-cell--editing .MuiSelect-select": {
                  backgroundColor: "#fff",
                },
              }}
            />
          </Box>
        </div>
        <Menu
          anchorEl={frozenMenuAnchorEl}
          open={Boolean(frozenMenuAnchorEl)}
          onClose={closeFrozenColumnMenu}
          MenuListProps={{ dense: true, disablePadding: true }}
          PaperProps={{
            sx: {
              mt: 0.5,
              minWidth: 180,
              borderRadius: 1,
              boxShadow: "0px 8px 24px rgba(15, 23, 42, 0.16)",
              border: "1px solid rgba(15, 23, 42, 0.08)",
            },
          }}
        >
          <Box sx={{ p: 1.25, width: 220 }} onClick={(event) => event.stopPropagation()}>
            <TextField
              value={frozenMenuField ? frozenFilters[frozenMenuField] || "" : ""}
              onChange={(event) => frozenMenuField && handleFrozenFilterChange(frozenMenuField, event.target.value)}
              placeholder={`Filter ${frozenMenuField || "column"}`}
              size="small"
              fullWidth
              autoFocus
            />
          </Box>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem dense onClick={() => handleFrozenColumnMenuSort("asc")}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <ArrowUpwardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Sort ascending</ListItemText>
          </MenuItem>
          <MenuItem dense onClick={() => handleFrozenColumnMenuSort("desc")}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <ArrowDownwardIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Sort descending</ListItemText>
          </MenuItem>
          <Divider sx={{ my: 0.5 }} />
          <MenuItem dense onClick={clearFrozenColumnSort}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <ClearIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Clear sort</ListItemText>
          </MenuItem>
          <MenuItem dense onClick={clearFrozenColumnFilter}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <ClearIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Clear filter</ListItemText>
          </MenuItem>
        </Menu>
        {!hideEmptyStateMessage && !batchId && !detailRows.length ? (
          <Box sx={{ color: "#4b5563", fontSize: 14, marginTop: 1 }}>
            Select a batch from approvals to view detail.
          </Box>
      ) : null}
      </div>

      <Dialog open={approveDialogOpen} onClose={handleApproveDialogClose}>
        <DialogTitle>Approve DC cost batch</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" sx={{ mt: 1 }}>
            <FormLabel component="legend" sx={{ color: "text.primary" }}>
              Do you need to use modified values?
            </FormLabel>
            <RadioGroup
              row
              value={approvalValueSource}
              onChange={(event) => setApprovalValueSource(event.target.value)}
            >
            <FormControlLabel value={APPROVAL_VALUE_SOURCES.PROPOSED} control={<Radio />} label="No" />
            <FormControlLabel value={APPROVAL_VALUE_SOURCES.MODIFY} control={<Radio />} label="Yes" />
            </RadioGroup>
          </FormControl>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Modified values will be used in cost file if selected on the screen otherwise proposed values will be used to generate cost.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Do you need to draft or approve this batch?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleApproveDialogClose} disabled={isApproving}>
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={() => void confirmApproveAction("draft")}
            disabled={isApproving}
          >
            {approveAction === "draft" && isApproving ? "Drafting..." : "Draft"}
          </Button>
          <Button
            variant="contained"
            onClick={() => void confirmApproveAction("approve")}
            disabled={isApproving}
          >
            {approveAction === "approve" && isApproving ? "Approving..." : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={actionDialog.open} onClose={closeRowAction}>
        <DialogTitle>
          {actionDialog.type ? `${actionDialog.type === "Block" ? "Block" : actionDialog.type} confirmation` : "Confirmation"}
        </DialogTitle>
        <DialogContent>
          {actionDialog.row ? (
            <Typography variant="body2">
              Are you sure you want to
              {` ${(actionDialog.type === "Block" ? "Block" : actionDialog.type || "").toLowerCase()} `}
              {actionDialog.row.parsedSku}?
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRowAction}>Cancel</Button>
          <Button variant="contained" onClick={confirmRowAction}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </section>
  );
};

export default DCCostBatchDetail;

